import type { ModerationTier } from './tiers';

export type EscalationThresholds = Record<ModerationTier, number>;

export interface RawEscalationThresholds {
  tier0: number;
  tier1: number;
  tier2: number;
  tier3: number;
  tier4: number;
}

/**
 * Raw (string-keyed) canonical defaults — matches the shape stored in site_configs
 * and used in defaultSiteConfigs (business-logic). DEFAULT_ESCALATION_THRESHOLDS is
 * derived from this; tune values here and both representations stay in sync.
 *
 * Lower values = more aggressive escalation (higher cost, higher recall on hard cases).
 * Higher values = more resolved at Layer 2 (lower cost, more risk on edge cases).
 */
export const DEFAULT_RAW_ESCALATION_THRESHOLDS: RawEscalationThresholds = {
  tier0: 0.4,
  tier1: 0.6,
  tier2: 0.7,
  tier3: 0.75,
  tier4: 0.8,
};

export const DEFAULT_ESCALATION_THRESHOLDS: EscalationThresholds = {
  0: DEFAULT_RAW_ESCALATION_THRESHOLDS.tier0,
  1: DEFAULT_RAW_ESCALATION_THRESHOLDS.tier1,
  2: DEFAULT_RAW_ESCALATION_THRESHOLDS.tier2,
  3: DEFAULT_RAW_ESCALATION_THRESHOLDS.tier3,
  4: DEFAULT_RAW_ESCALATION_THRESHOLDS.tier4,
};

export class EscalationThresholdValidationError extends Error {
  constructor(tier: ModerationTier, value: unknown) {
    super(`Escalation threshold for tier${tier} must be a finite number between 0 and 1, got ${value}`);
    this.name = 'EscalationThresholdValidationError';
  }
}

/**
 * Parses and validates raw threshold config (e.g. from site_configs).
 * Returns DEFAULT_ESCALATION_THRESHOLDS if raw is undefined.
 * Throws EscalationThresholdValidationError if any value is non-finite, NaN, or outside 0..1.
 */
export function parseEscalationThresholds(raw: RawEscalationThresholds | undefined): EscalationThresholds {
  if (!raw) return DEFAULT_ESCALATION_THRESHOLDS;

  const thresholds: EscalationThresholds = {
    0: raw.tier0,
    1: raw.tier1,
    2: raw.tier2,
    3: raw.tier3,
    4: raw.tier4,
  };

  for (const [tier, value] of Object.entries(thresholds) as [string, unknown][]) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) {
      throw new EscalationThresholdValidationError(Number(tier) as ModerationTier, value);
    }
  }

  return thresholds;
}
